import { useEffect, useState } from 'react';
import { LeadRatingPanel } from './LeadScoreBadge';
import { api } from '../api';
import {
  APPOINTMENT_OUTCOME_LABELS,
  OUTCOME_LABELS,
  OPPORTUNITY_STATUS_COLORS,
  type ContactJourneyResponse,
  type ContactTimelineEntry,
  type AppointmentOutcome,
  type OpportunityStatus,
  type Outcome,
  type TimelineKind,
} from '../types';
import { formatDateTime, formatDuration, formatNumber } from '../format';
import { Dialog } from './Dialog';
import { LeadSummaryPanel } from './AiSummary';

const KIND_LABELS: Record<TimelineKind, string> = {
  call: 'Call',
  appointment: 'Appointment',
  opportunity: 'Opportunity',
  message: 'Message',
  activity: 'Activity',
};

/** One hue per stream, drawn from the validated palette. */
const KIND_COLORS: Record<TimelineKind, string> = {
  call: 'var(--outcome-voicemail)',
  appointment: 'var(--outcome-answered)',
  opportunity: 'var(--outcome-no_answer)',
  message: 'var(--ink-muted)',
  activity: 'var(--accent)',
};

const OPPORTUNITY_EVENT_WORDS: Record<string, string> = {
  created: 'Opportunity created',
  stage_changed: 'Moved stage',
  status_changed: 'Status changed',
  value_changed: 'Value changed',
  assigned_changed: 'Reassigned',
  deleted: 'Opportunity deleted',
};

function headline(entry: ContactTimelineEntry): string {
  switch (entry.kind) {
    case 'call': {
      const outcome = entry.detail ? OUTCOME_LABELS[entry.detail as Outcome] : null;
      return `${entry.title ?? 'Call'} call — ${outcome ?? entry.detail ?? 'unknown outcome'}`;
    }
    case 'appointment':
      return entry.title ?? 'Appointment';
    case 'opportunity':
      return OPPORTUNITY_EVENT_WORDS[entry.title ?? ''] ?? (entry.title ?? 'Opportunity change');
    case 'activity':
      return 'Activity';
    case 'message':
    default:
      return (entry.title ?? 'Message').replace('TYPE_', '').replace(/_/g, ' ').toLowerCase();
  }
}

function subline(entry: ContactTimelineEntry): string | null {
  switch (entry.kind) {
    case 'call':
      return entry.meta ? `Lasted ${formatDuration(Number(entry.meta))}` : null;
    case 'appointment':
      return entry.detail
        ? (APPOINTMENT_OUTCOME_LABELS[entry.detail as AppointmentOutcome] ?? entry.detail)
        : null;
    case 'opportunity':
      return entry.meta && entry.detail
        ? `${entry.meta} → ${entry.detail}`
        : (entry.detail ?? null);
    default:
      // Activity and message bodies are HighLevel's own wording — shown as-is.
      return entry.detail;
  }
}

const FILTERS: { id: TimelineKind | 'all'; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'call', label: 'Calls' },
  { id: 'appointment', label: 'Appointments' },
  { id: 'opportunity', label: 'Opportunities' },
  { id: 'activity', label: 'Activity' },
  { id: 'message', label: 'Messages' },
];

export function ContactJourneyDrawer({
  contactId,
  onClose,
}: {
  contactId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<ContactJourneyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<TimelineKind | 'all'>('all');

  useEffect(() => {
    api
      .contactJourney(contactId)
      .then(setData)
      .catch((cause) => setError(String(cause)));
  }, [contactId]);

  const entries = data
    ? data.timeline.filter((entry) => kind === 'all' || entry.kind === kind)
    : [];

  return (
    <Dialog title={data?.contact.name ?? 'Contact journey'} onClose={onClose}>
      {error ? <div className="banner error">{error}</div> : null}
      {!data && !error ? <p className="empty">Loading journey…</p> : null}

      {data ? (
        <div className="stack">
          <div className="dialog-grid">
            <div>
              <div className="cell-sub">Contact</div>
              <a href={data.contact.url} target="_blank" rel="noreferrer">
                Open in HighLevel
              </a>
            </div>
            <div>
              <div className="cell-sub">Phone · email</div>
              <span className="small">
                {data.contact.phone ?? '—'}
                {data.contact.email ? ` · ${data.contact.email}` : ''}
              </span>
            </div>
          </div>

          <LeadRatingPanel key={data.contact.id} contactId={data.contact.id} lead={data.contact.leadScore} />

          <LeadSummaryPanel contactId={data.contact.id} />

          {data.contact.tags.length > 0 ? (
            <div className="chips">
              {data.contact.tags.map((tag) => (
                <span className="chip" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          {data.opportunities.length > 0 ? (
            <div className="row">
              {data.opportunities.map((opportunity) => (
                <span className="tag" key={opportunity.id}>
                  <span
                    className="dot"
                    style={{
                      background:
                        OPPORTUNITY_STATUS_COLORS[opportunity.status as OpportunityStatus] ??
                        'var(--ink-muted)',
                    }}
                    aria-hidden="true"
                  />
                  {opportunity.name ?? 'Opportunity'} · {formatNumber(Math.round(opportunity.monetaryValue))}
                </span>
              ))}
            </div>
          ) : null}

          <div className="chips">
            {FILTERS.map((filter) => {
              const count =
                filter.id === 'all'
                  ? data.timeline.length
                  : data.timeline.filter((entry) => entry.kind === filter.id).length;
              if (count === 0 && filter.id !== 'all') return null;
              return (
                <button
                  type="button"
                  className="chip"
                  key={filter.id}
                  aria-pressed={kind === filter.id}
                  onClick={() => setKind(filter.id)}
                >
                  {kind === filter.id ? '✓ ' : ''}
                  {filter.label} ({count})
                </button>
              );
            })}
          </div>

          <div className="stack" style={{ gap: 0 }}>
            {entries.map((entry) => (
              <div className="journey-row" key={`${entry.kind}-${entry.id}`}>
                <span className="journey-dot" style={{ background: KIND_COLORS[entry.kind] }} />
                <div className="journey-body">
                  <div className="spread">
                    <span className="cell-strong">
                      {KIND_LABELS[entry.kind]} · {headline(entry)}
                    </span>
                    <span className="note-meta">{formatDateTime(entry.occurredAt)}</span>
                  </div>
                  {subline(entry) ? <div className="note-body">{subline(entry)}</div> : null}
                  {entry.actorName ? <div className="cell-sub">by {entry.actorName}</div> : null}
                </div>
              </div>
            ))}
            {entries.length === 0 ? <p className="empty">Nothing recorded for this contact yet.</p> : null}
          </div>

          <p className="small muted">
            Built from everything already synced for this contact — calls, appointments, opportunity
            transitions, messages, and HighLevel’s own activity entries. Newest first.
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
